import {
  CUSTOM_ELEMENTS_SCHEMA,
  Component,
  DestroyRef,
  Injector,
  OnInit,
  afterNextRender,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { finalize, forkJoin } from 'rxjs';
import {
  AlteracaoExtrato,
  CondicaoRegra,
  ExtratoDesligamentoConfiguracao,
  InstitutoExtrato,
  OpcaoCondicao,
  OpcaoSituacao,
  RegraElegibilidade,
} from '../../../core/models/parametros/extrato-desligamento.model';
import { PlanoResumo } from '../../../core/models/parametros/plano.model';
import { ExtratoDesligamentoService } from '../../../core/services/parametros/extrato-desligamento.service';
import { PlanosService } from '../../../core/services/parametros/planos.service';
import { ToastService } from '../../../core/services/toast.service';
import { descreverCondicao, INSTITUTOS_EXTRATO_INFO } from '../../../core/utils/parametros/extrato-desligamento.defaults';
import { formatDateIsoToBr } from '../../../core/utils/date-format.util';
import { PlanoCabecalhoComponent } from '../../../shared/components/parametros/plano-cabecalho/plano-cabecalho.component';
import { VivestModalAreaVisivelDirective } from '../../../shared/directives/modal-vivest/vivest-modal-area-visivel.directive';
import { LoaderService } from '../../../shared/services/loader/loader.service';
import { RegraEditada, RegraElegibilidadeModalComponent } from './regra-elegibilidade-modal/regra-elegibilidade-modal.component';
import { SituacoesCadastraisModalComponent } from './situacoes-cadastrais-modal/situacoes-cadastrais-modal.component';
import { CalendarizacaoNotificacoesComponent } from './calendarizacao-notificacoes/calendarizacao-notificacoes.component';

type AbaExtrato = 'elegibilidade' | 'notificacoes';

const ABAS_EXTRATO: readonly { id: AbaExtrato; rotulo: string }[] = [
  { id: 'elegibilidade', rotulo: 'Critérios de Elegibilidade' },
  { id: 'notificacoes', rotulo: 'Calendarização de Notificações' },
];

/** Regra aberta no modal: o instituto (`id_inst_param`) e a regra em edição (`null` é uma regra nova). */
interface RegraEmEdicao {
  idInstParam: number;
  regra: RegraElegibilidade | null;
}

/**
 * Parametrização de elegibilidade do extrato de desligamento do plano, aberta pela aba "Instituto":
 * institutos habilitados com as situações cadastrais e as regras de cada um (condições e mensagem
 * de não elegibilidade), adesão automática por perda de prazo e a última alteração salva. As mudanças
 * só são gravadas em "Salvar parâmetros" (com confirmação).
 */
@Component({
  selector: 'app-extrato-desligamento',
  standalone: true,
  imports: [
    PlanoCabecalhoComponent,
    CalendarizacaoNotificacoesComponent,
    RegraElegibilidadeModalComponent,
    SituacoesCadastraisModalComponent,
    VivestModalAreaVisivelDirective,
  ],
  // Web components do design system Vivest (card, checkbox, switch, select, button, icon-button, modal).
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './extrato-desligamento.component.html',
  styleUrl: './extrato-desligamento.component.scss',
})
export class ExtratoDesligamentoComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly extratoService = inject(ExtratoDesligamentoService);
  private readonly planosService = inject(PlanosService);
  private readonly loaderService = inject(LoaderService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  /** Id da parametrização do plano (`/planos/:id/extrato-desligamento`). */
  readonly planoId = Number(inject(ActivatedRoute).snapshot.paramMap.get('id'));


  readonly plano = signal<PlanoResumo | null>(null);
  readonly prazoOpcaoDias = signal<number | null>(null);
  /** Alteração mais recente, exibida no topo da tela. */
  readonly ultimaAlteracao = signal<AlteracaoExtrato | null>(null);

  /** Catálogos da API, na ordem da tela. */
  readonly situacoes = signal<OpcaoSituacao[]>([]);
  readonly condicoes = signal<OpcaoCondicao[]>([]);
  private readonly rotulosSituacao = computed(
    () => new Map(this.situacoes().map((opcao) => [opcao.value, opcao.label])),
  );

  /** Último estado carregado ou salvo, para o "Descartar alterações". */
  private readonly carregado = signal<ExtratoDesligamentoConfiguracao | null>(null);
  /** O que está na tela. */
  readonly rascunho = signal<ExtratoDesligamentoConfiguracao | null>(null);

  readonly abas = ABAS_EXTRATO;
  readonly abaAtiva = signal<AbaExtrato>('elegibilidade');
  /** A aba "Calendarização de Notificações" só carrega quando é aberta pela primeira vez. */
  readonly notificacoesAberta = signal(false);

  /** Instituto (`id_inst_param`) com o modal de situação cadastral aberto, para adicionar ou editar. */
  readonly selecionandoSituacoes = signal<{ idInstParam: number; modo: 'adicionar' | 'editar' } | null>(null);
  readonly regraEmEdicao = signal<RegraEmEdicao | null>(null);
  readonly confirmandoSalvar = signal(false);
  readonly salvando = signal(false);
  readonly tentouSalvar = signal(false);

  readonly temAlteracoes = computed(
    () => !!this.rascunho() && JSON.stringify(this.rascunho()) !== JSON.stringify(this.carregado()) && !this.salvando(),
  );

  /**
   * O "Salvar parâmetros" só habilita com tudo preenchido; por isso os erros aparecem assim que há
   * alteração na tela (ou depois de uma tentativa de salvar), para dizer o que falta.
   */
  readonly mostrarErros = computed(() => this.tentouSalvar() || this.temAlteracoes());

  /** Institutos habilitados sem situação cadastral marcada (`id_inst_param`). */
  readonly institutosSemSituacao = computed(
    () =>
      new Set(
        (this.rascunho()?.institutos ?? [])
          .filter((instituto) => instituto.habilitado && !instituto.situacoesCadastrais.length)
          .map((instituto) => instituto.idInstParam),
      ),
  );

  /** Situações já marcadas no instituto do modal aberto. */
  readonly situacoesDoModal = computed(() => {
    const idInstParam = this.selecionandoSituacoes()?.idInstParam;
    return this.rascunho()?.institutos.find((instituto) => instituto.idInstParam === idInstParam)?.situacoesCadastrais ?? [];
  });

  /** Institutos que podem ser o padrão da adesão automática: só os habilitados (o select trabalha com texto). */
  readonly opcoesInstitutoPadrao = computed(() =>
    (this.rascunho()?.institutos ?? [])
      .filter((instituto) => instituto.habilitado)
      .map((instituto) => ({ value: String(instituto.idInstParam), label: this.nome(instituto) })),
  );

  readonly erroInstitutoPadrao = computed(() => {
    const adesao = this.rascunho()?.adesaoAutomatica;
    if (!adesao?.habilitada) {
      return '';
    }
    if (!adesao.institutoPadrao) {
      return 'Escolha o instituto aplicado na adesão automática.';
    }
    return this.opcoesInstitutoPadrao().some((opcao) => opcao.value === String(adesao.institutoPadrao))
      ? ''
      : 'O instituto escolhido está desabilitado. Habilite-o ou escolha outro.';
  });

  readonly valido = computed(() => !this.institutosSemSituacao().size && !this.erroInstitutoPadrao());

  /** Nome do instituto da regra aberta no modal. */
  readonly institutoEmEdicao = computed(() => {
    const edicao = this.regraEmEdicao();
    const instituto = edicao && this.rascunho()?.institutos.find((item) => item.idInstParam === edicao.idInstParam);
    return instituto ? this.nomeCurto(instituto) : '';
  });

  ngOnInit(): void {
    this.loaderService.showLoader();
    forkJoin({
      plano: this.planosService.obterPlano(this.planoId),
      extrato: this.extratoService.obter(this.planoId),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ plano, extrato }) => {
          this.plano.set(plano);
          this.situacoes.set(extrato.situacoes);
          this.condicoes.set(extrato.condicoes);
          this.prazoOpcaoDias.set(extrato.prazoOpcaoDias);
          this.ultimaAlteracao.set(extrato.ultimaAlteracao);
          this.aplicar(extrato.configuracao);
          afterNextRender(() => this.loaderService.hideLoader(), { injector: this.injector });
        },
        error: () => {
          this.loaderService.hideLoader();
          this.toastService.show('Não foi possível carregar a parametrização do extrato de desligamento.', 'error');
        },
      });
  }

  // ── textos ──────────────────────────────────────────────────────────────────

  /** Tipos conhecidos usam o nome da tela; os demais, o nome de exibição da API. */
  nome(instituto: InstitutoExtrato): string {
    return instituto.tipo ? INSTITUTOS_EXTRATO_INFO[instituto.tipo].nome : instituto.nomeExibicao;
  }

  descricao(instituto: InstitutoExtrato): string {
    return instituto.tipo ? INSTITUTOS_EXTRATO_INFO[instituto.tipo].descricao : '';
  }

  /** "Benefício Proporcional Diferido (BPD)" vira "BPD" nos títulos e rótulos curtos. */
  nomeCurto(instituto: InstitutoExtrato): string {
    const nome = this.nome(instituto);
    return /\(([^)]+)\)$/.exec(nome)?.[1] ?? nome;
  }

  condicao(condicao: CondicaoRegra): string {
    return descreverCondicao(condicao, this.condicoes());
  }

  /** `2026-09-28T14:32:00` → `28/09/2026 às 14:32`. */
  dataHora(iso: string): string {
    const [data, hora = ''] = iso.split('T');
    return `${formatDateIsoToBr(data)} às ${hora.slice(0, 5)}`;
  }

  // ── abas ────────────────────────────────────────────────────────────────────

  selecionarAba(aba: AbaExtrato): void {
    this.abaAtiva.set(aba);
    if (aba === 'notificacoes') {
      this.notificacoesAberta.set(true);
    }
  }

  // ── situações cadastrais (por instituto) ────────────────────────────────────

  rotuloSituacao(situacao: number): string {
    return this.rotulosSituacao().get(situacao) ?? String(situacao);
  }

  adicionarSituacao(instituto: InstitutoExtrato): void {
    this.selecionandoSituacoes.set({ idInstParam: instituto.idInstParam, modo: 'adicionar' });
  }

  editarSituacoes(instituto: InstitutoExtrato): void {
    this.selecionandoSituacoes.set({ idInstParam: instituto.idInstParam, modo: 'editar' });
  }

  /** Como a regra: sai do rascunho e só vale ao salvar. Habilitado sem situação não salva. */
  excluirSituacoes(instituto: InstitutoExtrato): void {
    this.alterarInstituto(instituto.idInstParam, (item) => ({ ...item, situacoesCadastrais: [] }));
    this.toastService.show(`Situação cadastral de ${this.nomeCurto(instituto)} excluída. Salve para confirmar.`, 'success');
  }

  /**
   * Retorno do modal: nos dois modos ele é editável (as já incluídas podem ser desmarcadas) e
   * devolve a lista inteira, que substitui a do instituto.
   */
  adicionarSituacoes(situacoes: number[]): void {
    const selecao = this.selecionandoSituacoes();
    if (!selecao) {
      return;
    }
    this.alterarInstituto(selecao.idInstParam, (item) => ({
      ...item,
      situacoesCadastrais: this.naOrdemDaTela(new Set(situacoes)),
    }));
    this.selecionandoSituacoes.set(null);
  }

  // ── institutos e regras ─────────────────────────────────────────────────────

  habilitar(instituto: InstitutoExtrato, habilitado: boolean): void {
    this.alterarInstituto(instituto.idInstParam, (item) => ({ ...item, habilitado }));
  }

  adicionarRegra(instituto: InstitutoExtrato): void {
    this.regraEmEdicao.set({ idInstParam: instituto.idInstParam, regra: null });
  }

  editarRegra(instituto: InstitutoExtrato, regra: RegraElegibilidade): void {
    this.regraEmEdicao.set({ idInstParam: instituto.idInstParam, regra });
  }

  excluirRegra(instituto: InstitutoExtrato, regra: RegraElegibilidade): void {
    this.alterarInstituto(instituto.idInstParam, (item) => ({
      ...item,
      regras: item.regras.filter((atual) => atual.id !== regra.id),
    }));
    this.toastService.show(`Regra de ${this.nomeCurto(instituto)} excluída. Salve para confirmar.`, 'success');
  }

  fecharRegra(): void {
    this.regraEmEdicao.set(null);
  }

  salvarRegra(editada: RegraEditada): void {
    const edicao = this.regraEmEdicao();
    if (!edicao) {
      return;
    }
    this.alterarInstituto(edicao.idInstParam, (instituto) => ({
      ...instituto,
      regras: edicao.regra
        ? instituto.regras.map((item) => (item.id === edicao.regra!.id ? { ...item, ...editada } : item))
        : [...instituto.regras, { id: novoId(edicao.idInstParam), idApi: null, ...editada }],
    }));
    this.regraEmEdicao.set(null);
  }

  // ── adesão automática ───────────────────────────────────────────────────────

  marcarAdesaoAutomatica(habilitada: boolean): void {
    this.alterar((config) => ({ ...config, adesaoAutomatica: { ...config.adesaoAutomatica, habilitada } }));
  }

  escolherInstitutoPadrao(valor: string): void {
    const institutoPadrao = valor ? Number(valor) : null;
    this.alterar((config) => ({ ...config, adesaoAutomatica: { ...config.adesaoAutomatica, institutoPadrao } }));
  }

  // ── salvar / descartar ──────────────────────────────────────────────────────

  salvar(): void {
    this.tentouSalvar.set(true);
    if (this.temAlteracoes() && this.valido()) {
      this.confirmandoSalvar.set(true);
    }
  }

  cancelarSalvar(): void {
    this.confirmandoSalvar.set(false);
  }

  confirmarSalvar(): void {
    this.confirmandoSalvar.set(false);
    const configuracao = this.rascunho();
    if (!configuracao || !this.valido() || this.salvando()) {
      return;
    }
    this.salvando.set(true);
    this.loaderService.showLoader();
    this.extratoService
      .salvar(this.planoId, configuracao, this.situacoes())
      .pipe(
        finalize(() => {
          this.salvando.set(false);
          this.loaderService.hideLoader();
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ configuracao: salva, ultimaAlteracao }) => {
          this.ultimaAlteracao.set(ultimaAlteracao);
          this.aplicar(salva);
          this.toastService.show('Parâmetros do extrato de desligamento salvos.', 'success');
        },
        error: () => this.toastService.show('Não foi possível salvar os parâmetros do extrato de desligamento.', 'error'),
      });
  }

  /** "Descartar alterações": volta ao último estado carregado ou salvo. */
  descartar(): void {
    const carregado = this.carregado();
    if (carregado) {
      this.aplicar(carregado);
    }
  }

  /** Volta para a aba "Instituto" do plano, de onde a tela foi aberta. */
  voltar(): void {
    void this.router.navigate(['/planos', this.planoId, 'editar'], { queryParams: { aba: 'instituto' } });
  }

  /** "Voltar para a lista" do cabeçalho: volta para a listagem de planos. */
  voltarParaLista(): void {
    void this.router.navigate(['/planos']);
  }

  // ── estado ──────────────────────────────────────────────────────────────────

  private aplicar(configuracao: ExtratoDesligamentoConfiguracao): void {
    this.carregado.set(structuredClone(configuracao));
    this.rascunho.set(structuredClone(configuracao));
    this.tentouSalvar.set(false);
  }

  private alterar(mudanca: (config: ExtratoDesligamentoConfiguracao) => ExtratoDesligamentoConfiguracao): void {
    const atual = this.rascunho();
    if (atual) {
      this.rascunho.set(mudanca(atual));
    }
  }

  private alterarInstituto(idInstParam: number, mudanca: (instituto: InstitutoExtrato) => InstitutoExtrato): void {
    this.alterar((config) => ({
      ...config,
      institutos: config.institutos.map((instituto) => (instituto.idInstParam === idInstParam ? mudanca(instituto) : instituto)),
    }));
  }

  /** Situações cadastrais na ordem do catálogo, não na ordem em que foram escolhidas. */
  private naOrdemDaTela(situacoes: ReadonlySet<number>): number[] {
    return this.situacoes()
      .map((opcao) => opcao.value)
      .filter((situacao) => situacoes.has(situacao));
  }
}

let sequencia = 0;

/** Id local de uma regra nova; a API gera o id definitivo ao salvar. */
function novoId(idInstParam: number): string {
  sequencia += 1;
  return `nova-${idInstParam}-${Date.now()}-${sequencia}`;
}
